/* Shopify-native cinematic book sampler. No purchase or theme-global dependencies. */
(() => {
  const elementName = 'md-cinematic-sampler-v2';
  if (customElements.get(elementName)) return;

  class MdCinematicSamplerV2 extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;

      this.lifecycle = new AbortController();
      this.positions ||= new Map();
      this.expandedBooks ||= new Set();
      this.mobileQuery = window.matchMedia('(max-width: 1080px)');
      this.mode ||= this.dataset.defaultMode === 'read' ? 'read' : 'listen';
      this.speed ||= 1;
      this.captionsEnabled ??= true;
      this.mediaEpoch ||= 0;
      this.playRequest ||= 0;
      this.getPart = key => this.querySelector(`[data-mdsv2-${key}]`);
      this.stage = this.getPart('stage');
      this.reader = this.getPart('copy');

      const listen = (node, type, callback) => {
        if (node) node.addEventListener(type, callback, { signal: this.lifecycle.signal });
      };

      const tabs = [...this.querySelectorAll('[data-mdsv2-tab]')];
      tabs.forEach((tab, index) => {
        listen(tab, 'click', () => this.setMode(tab.dataset.mdsv2Tab));
        listen(tab, 'keydown', event => {
          const target = {
            ArrowLeft: (index - 1 + tabs.length) % tabs.length,
            ArrowRight: (index + 1) % tabs.length,
            Home: 0,
            End: tabs.length - 1
          }[event.key];
          if (target === undefined) return;
          event.preventDefault();
          this.setMode(tabs[target].dataset.mdsv2Tab);
          tabs[target].focus();
        });
      });

      listen(this.getPart('previous'), 'click', () => this.move(-1));
      listen(this.getPart('next'), 'click', () => this.move(1));
      listen(this.getPart('dots'), 'click', event => {
        const button = event.target.closest('[data-mdsv2-select]');
        if (button) this.select(button.dataset.mdsv2Select);
      });
      listen(this.getPart('read-toggle'), 'click', () => {
        const expanded = !this.expandedBooks.has(this.activeId);
        if (expanded) this.expandedBooks.add(this.activeId);
        else this.expandedBooks.delete(this.activeId);
        this.renderReader();
        if (!expanded) this.getPart('reader').scrollIntoView({ block: 'start', behavior: 'instant' });
      });
      listen(this.stage, 'click', event => {
        const button = event.target.closest('[data-mdsv2-cover-select]');
        if (button && !this.suppressCoverClick) this.select(button.dataset.mdsv2CoverSelect);
        this.suppressCoverClick = false;
      });

      listen(this.stage, 'keydown', event => {
        if (event.target !== this.stage || !this.books?.length) return;
        const target = {
          ArrowLeft: this.index - 1,
          ArrowRight: this.index + 1,
          Home: 0,
          End: this.books.length - 1
        }[event.key];
        if (target === undefined) return;
        event.preventDefault();
        this.selectIndex(target);
      });
      listen(this.stage, 'dragstart', event => event.preventDefault());
      listen(this.stage, 'pointerdown', event => {
        if (!event.isPrimary || event.button !== 0 || event.target.closest('.mdsv2__book-arrow') || event.target.closest('.mdsv2__phone')) return;
        this.suppressCoverClick = false;
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY };
      });
      listen(this.stage, 'pointerup', event => {
        const drag = this.drag;
        this.drag = null;
        if (!drag || drag.id !== event.pointerId) return;
        try {
          if (this.stage.hasPointerCapture?.(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);
        } catch (_) { /* Capture may already have been released by the browser. */ }
        const deltaX = event.clientX - drag.x;
        const deltaY = event.clientY - drag.y;
        if (Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY) * 1.4) {
          this.suppressCoverClick = true;
          this.move(deltaX < 0 ? 1 : -1);
        }
      });
      listen(this.stage, 'pointercancel', () => { this.drag = null; });

      listen(this.getPart('play'), 'click', () => this.togglePlay());
      listen(this.getPart('start'), 'click', () => this.seek(0));
      listen(this.getPart('end'), 'click', () => this.seek(this.duration));
      listen(this.getPart('rewind'), 'click', () => this.seek((this.audio?.currentTime || 0) - 15));
      listen(this.getPart('forward'), 'click', () => this.seek((this.audio?.currentTime || 0) + 15));
      listen(this.getPart('progress'), 'input', event => this.seek(Number(event.target.value)));
      listen(this.getPart('speed'), 'click', () => {
        const speeds = [1, 1.25, 1.5, 2];
        this.speed = speeds[(speeds.indexOf(this.speed) + 1) % speeds.length];
        if (this.audio) this.audio.playbackRate = this.speed;
        this.render();
      });
      listen(this.getPart('cc'), 'click', () => {
        this.captionsEnabled = !this.captionsEnabled;
        this.render();
      });

      listen(document, 'shopify:block:select', event => {
        if (event.detail?.sectionId && event.detail.sectionId !== this.dataset.sectionId) return;
        if (event.target !== document && !this.contains(event.target)) return;
        if (this.books?.some(book => book.id === event.detail?.blockId)) this.select(event.detail.blockId);
      });
      listen(document, 'shopify:section:unload', event => {
        const matches = event.detail?.sectionId
          ? event.detail.sectionId === this.dataset.sectionId
          : event.target !== document && event.target.contains(this);
        if (matches) this.disconnectedCallback();
      });

      this.readBooks();
      this.setMode(this.mode);
      this.setupNavigationContext(listen);
      this.observer = new MutationObserver(() => this.readBooks());
      this.observer.observe(this.stage, { childList: true });
      this.dataset.mdsv2Ready = 'true';
    }

    disconnectedCallback() {
      if (!this.lifecycle) return;
      this.savePosition();
      this.disposeMedia();
      this.lifecycle.abort();
      this.lifecycle = null;
      this.observer?.disconnect();
      this.navigationObserver?.disconnect();
      this.navigationResize?.disconnect();
      cancelAnimationFrame(this.navigationFrame);
      this.clearNavigationContext();
      cancelAnimationFrame(this.scrollFrame);
      this.drag = null;
      delete this.dataset.mdsv2Ready;
    }

    readBooks() {
      this.books = [...this.stage.querySelectorAll('[data-mdsv2-book]')].slice(0, 8).map(element => ({
        id: element.dataset.mdsv2Book,
        element,
        title: element.dataset.title || 'Untitled sample',
        author: element.dataset.author || 'Myra Danvers',
        series: element.dataset.series || '',
        chapter: element.dataset.chapter || '',
        teaser: element.dataset.teaser || '',
        warning: element.dataset.warning || '',
        tropes: element.dataset.tropes || '',
        storyEyebrow: element.dataset.storyEyebrow || '',
        storyHeading: element.dataset.storyHeading || '',
        storyAccent: element.dataset.storyAccent || '',
        proofQuote: element.dataset.proofQuote || '',
        proofSource: element.dataset.proofSource || '',
        proofRating: Math.max(0, Math.min(5, Number(element.dataset.proofRating) || 0)),
        audioUrl: this.safeUrl(element.dataset.audio),
        captionsUrl: this.safeUrl(element.dataset.captions),
        fallbackDuration: Math.max(0, Number(element.dataset.duration) || 0),
        ctaLabel: element.dataset.cta || '',
        ctaUrl: this.safeUrl(element.dataset.href),
        readingTemplate: element.querySelector('[data-mdsv2-read-template]'),
        audioEndTemplate: element.querySelector('[data-mdsv2-audio-end-template]'),
        artworkTemplate: element.querySelector('[data-mdsv2-art-template]'),
        storyTemplate: element.querySelector('[data-mdsv2-story-template]')
      }));

      this.getPart('empty').hidden = this.books.length > 0;
      this.getPart('layout').hidden = this.books.length === 0;
      this.getPart('dots').replaceChildren(...this.books.map(book => {
        const button = document.createElement('button');
        const dot = document.createElement('span');
        button.type = 'button';
        button.dataset.mdsv2Select = book.id;
        button.setAttribute('aria-label', `Select ${book.title}`);
        dot.setAttribute('aria-hidden', 'true');
        button.append(dot);
        return button;
      }));

      if (this.books.length) {
        const selected = this.books.some(book => book.id === this.activeId) ? this.activeId : this.books[0].id;
        this.select(selected, true);
      } else {
        this.savePosition();
        this.disposeMedia();
        this.activeId = null;
      }
    }

    safeUrl(value) {
      if (!value?.trim()) return '';
      try {
        const url = new URL(value, document.baseURI);
        return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
      } catch (_) {
        return '';
      }
    }

    get index() {
      return this.books.findIndex(book => book.id === this.activeId);
    }

    get duration() {
      // Respect the editor's sample boundary; VBR metadata can drift after seeking.
      if (this.book?.fallbackDuration > 0) return this.book.fallbackDuration;
      if (Number.isFinite(this.audio?.duration) && this.audio.duration > 0) return this.audio.duration;
      return 0;
    }

    get sampleEnded() {
      return Boolean(this.audio && (this.audio.ended || (this.duration > 0 && this.audio.currentTime >= this.duration - .05)));
    }

    get canSeek() {
      return Boolean(
        this.audio &&
        this.audio.readyState >= 1 &&
        Number.isFinite(this.audio.duration) &&
        this.audio.duration > 0
      );
    }

    selectIndex(index) {
      if (!this.books.length) return;
      this.select(this.books[(index + this.books.length) % this.books.length].id);
    }

    move(delta) {
      this.selectIndex(this.index + delta);
    }

    savePosition() {
      if (!this.activeId) return;
      const previous = this.positions.get(this.activeId) || {};
      this.positions.set(this.activeId, {
        time: this.restoreTime ?? (Number.isFinite(this.audio?.currentTime) ? this.audio.currentTime : previous.time || 0),
        scroll: this.pendingScroll?.id === this.activeId
          ? this.pendingScroll.value
          : this.mode === 'read' && !this.reader?.hidden
            ? this.reader.scrollTop
            : previous.scroll || 0
      });
    }

    disposeMedia() {
      this.mediaEpoch += 1;
      this.playRequest += 1;
      this.mediaLifecycle?.abort();
      this.captionRequest?.abort();
      clearTimeout(this.loadTimer);
      clearTimeout(this.captionTimer);
      if (this.audio) {
        this.audio.pause();
        this.audio.removeAttribute('src');
        this.audio.load();
        this.audio.remove();
        this.audio = null;
      }
      this.cues = [];
      this.restoreTime = null;
    }

    select(id, force = false) {
      const book = this.books.find(candidate => candidate.id === id);
      if (!book || (!force && id === this.activeId)) return;

      this.savePosition();
      this.disposeMedia();
      this.activeId = id;
      this.book = book;
      const epoch = this.mediaEpoch;
      const activeIndex = this.index;

      this.books.forEach((candidate, index) => {
        const total = this.books.length;
        const offset = (index - activeIndex + total) % total;

        let position = 'hidden';

        if (offset === 0) {
          position = 'active';
        } else if (offset === 1) {
          position = 'next';
        } else if (offset === total - 1) {
          position = 'previous';
        } else if (offset === 2) {
          position = 'next-far';
        } else if (offset === total - 2) {
          position = 'previous-far';
        }

        candidate.element.dataset.mdsv2Position = position;
        candidate.element.setAttribute('aria-hidden', String(position === 'hidden'));
        const cover = candidate.element.querySelector('[data-mdsv2-cover-select]');
        if (cover) cover.tabIndex = position === 'hidden' ? -1 : 0;
      });

      this.getPart('dots').querySelectorAll('button').forEach(button => {
        button.setAttribute('aria-current', String(button.dataset.mdsv2Select === id));
      });

      ['title', 'author', 'series', 'chapter', 'teaser', 'warning', 'storyEyebrow', 'storyHeading', 'storyAccent', 'proofQuote', 'proofSource'].forEach(key => {
        this.querySelectorAll(`[data-mdsv2-text="${key}"]`).forEach(element => {
          element.textContent = book[key] || '';
          element.hidden = !book[key];
        });
      });

      this.getPart('selection').textContent = `${book.title}. Sample ${activeIndex + 1} of ${this.books.length}.`;
      this.getPart('previous').disabled = this.books.length < 2;
      this.getPart('next').disabled = this.books.length < 2;

      const cta = this.getPart('cta');
      if (cta) {
        const label = cta.dataset.label || book.ctaLabel;
        this.getPart('purchase').hidden = !(label && book.ctaUrl);
        cta.querySelector('span').textContent = label;
        if (book.ctaUrl) cta.href = book.ctaUrl;
        else cta.removeAttribute('href');
      }

      this.getPart('copy').replaceChildren(
        book.readingTemplate?.content.cloneNode(true) || document.createTextNode('')
      );
      this.getPart('art').replaceChildren(
        book.artworkTemplate?.content.cloneNode(true) || document.createTextNode('')
      );
      this.getPart('art').classList.remove('mdsv2__listener-art--ending');
      const prose = this.reader.querySelector('.mdsv2__reading-prose');
      if (prose && !prose.textContent.trim()) prose.textContent = 'A reading sample is not available for this book yet.';
      this.renderReader();

      const tropeElements = book.tropes
        .split(',')
        .map(trope => trope.trim())
        .filter(Boolean)
        .slice(0, 12)
        .map(trope => {
          const pill = document.createElement('span');
          pill.textContent = trope;
          return pill;
        });
      this.getPart('tropes').replaceChildren(...tropeElements);
      this.getPart('tropes').closest('section').hidden = tropeElements.length === 0;

      const storyBody = this.getPart('story-body');
      storyBody.replaceChildren(book.storyTemplate?.content.cloneNode(true) || document.createTextNode(''));
      storyBody.hidden = !storyBody.textContent.trim();

      const stars = this.getPart('stars');
      stars.replaceChildren(...Array.from({ length: book.proofRating }, () => {
        const star = document.createElement('span');
        star.setAttribute('aria-hidden', 'true');
        star.textContent = '★';
        return star;
      }));
      stars.hidden = book.proofRating === 0;
      stars.setAttribute('aria-label', book.proofRating ? `${book.proofRating} out of 5 stars` : 'No rating');
      stars.closest('section').hidden = !book.proofQuote && !book.proofSource && !book.proofRating;
      this.restoreScrollPosition();

      this.mediaError = '';
      this.mediaState = book.audioUrl ? 'loading' : 'unavailable';
      this.captionState = book.captionsUrl ? 'loading' : 'missing';

      if (book.audioUrl) this.loadAudio(book, epoch);
      if (book.captionsUrl) this.loadCaptions(book.captionsUrl, epoch);
      this.render();
    }

    loadAudio(book, epoch) {
      const audio = document.createElement('audio');
      audio.preload = 'metadata';
      audio.playsInline = true;
      audio.hidden = true;
      audio.dataset.mdsv2Audio = '';
      this.getPart('media').replaceChildren(audio);
      this.audio = audio;
      this.mediaLifecycle = new AbortController();

      const isCurrent = () => this.isConnected && this.mediaEpoch === epoch && this.audio === audio;
      const listen = (eventName, callback) => {
        audio.addEventListener(eventName, () => { if (isCurrent()) callback(); }, { signal: this.mediaLifecycle.signal });
      };

      this.restoreTime = this.positions.get(book.id)?.time || 0;
      const ready = () => {
        if (audio.readyState < 1) return;
        clearTimeout(this.loadTimer);
        if (this.restoreTime !== null && this.canSeek) {
          try {
            audio.currentTime = Math.min(this.restoreTime, this.duration);
            this.restoreTime = null;
          } catch (_) {
            return;
          }
        }
        audio.playbackRate = this.speed;
        if (!this.mediaError) this.mediaState = this.sampleEnded ? 'ended' : audio.paused ? 'paused' : 'playing';
        this.render();
      };

      listen('loadedmetadata', ready);
      listen('canplay', ready);
      listen('durationchange', ready);
      listen('timeupdate', () => {
        if (this.sampleEnded) {
          audio.pause();
          this.mediaState = 'ended';
        }
        this.render();
      });
      listen('seeked', () => this.render());
      listen('playing', () => {
        clearTimeout(this.loadTimer);
        this.mediaError = '';
        this.mediaState = 'playing';
        this.render();
      });
      listen('pause', () => {
        if (!this.mediaError) this.mediaState = this.sampleEnded ? 'ended' : 'paused';
        this.render();
      });
      listen('waiting', () => {
        if (!this.mediaError) this.mediaState = 'loading';
        this.render();
      });
      listen('ended', () => {
        this.mediaState = 'ended';
        this.render();
      });
      listen('error', () => {
        clearTimeout(this.loadTimer);
        this.mediaError = 'Audio could not be loaded. Select another sample or try again later.';
        this.mediaState = 'error';
        this.render();
      });

      this.loadTimer = setTimeout(() => {
        if (isCurrent() && audio.readyState < 1) {
          this.mediaError = 'Audio is taking too long to load. Check your connection and reselect this book.';
          this.mediaState = 'error';
          this.render();
        }
      }, 20000);

      audio.src = book.audioUrl;
      audio.load();
    }

    restoreScrollPosition() {
      cancelAnimationFrame(this.scrollFrame);
      const id = this.activeId;
      const value = this.positions.get(id)?.scroll || 0;
      this.pendingScroll = { id, value };
      this.scrollFrame = requestAnimationFrame(() => {
        if (this.activeId === id && this.mode === 'read' && !this.mobileQuery.matches) this.reader.scrollTop = value;
        this.pendingScroll = null;
      });
    }

    setMode(mode) {
      this.savePosition();
      this.mode = mode === 'read' ? 'read' : 'listen';
      this.dataset.mdsv2Mode = this.mode;
      this.playRequest += 1;
      if (this.mode === 'read') this.audio?.pause();

      this.querySelectorAll('[data-mdsv2-tab]').forEach(tab => {
        const selected = tab.dataset.mdsv2Tab === this.mode;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.querySelectorAll('[data-mdsv2-panel]').forEach(panel => {
        const selected = panel.dataset.mdsv2Panel === this.mode;
        panel.hidden = !selected;
        panel.inert = !selected;
      });
      this.restoreScrollPosition();
      this.render();
    }

    async togglePlay() {
      const audio = this.audio;
      if (!audio || this.mode !== 'listen') return;
      const request = ++this.playRequest;
      const epoch = this.mediaEpoch;

      if (!audio.paused) {
        audio.pause();
        return;
      }
      if (this.restoreTime !== null && this.restoreTime > 0) return;
      if (audio.ended || (this.canSeek && audio.currentTime >= this.duration - .05)) this.seek(0);

      this.mediaError = '';
      this.mediaState = 'loading';
      this.render();
      try {
        await audio.play();
        if (this.mediaEpoch !== epoch || request !== this.playRequest || this.mode !== 'listen' || !this.isConnected) {
          if (this.audio !== audio || this.mode !== 'listen' || !this.isConnected) audio.pause();
          return;
        }
        this.mediaState = 'playing';
        this.render();
      } catch (error) {
        if (this.mediaEpoch !== epoch || request !== this.playRequest) return;
        this.mediaError = error.name === 'NotAllowedError'
          ? 'Playback was blocked. Press play to try again.'
          : 'Playback failed. Press play to retry.';
        this.mediaState = 'error';
        this.render();
      }
    }

    seek(seconds) {
      if (!this.canSeek || !Number.isFinite(seconds)) return;
      try {
        this.audio.currentTime = Math.max(0, Math.min(this.duration, seconds));
        this.restoreTime = null;
        if (this.sampleEnded) {
          this.audio.pause();
          this.mediaState = 'ended';
        } else if (this.mediaState === 'ended') this.mediaState = this.audio.paused ? 'paused' : 'playing';
      } catch (_) {
        return;
      }
      this.render();
    }

    async loadCaptions(url, epoch) {
      this.captionRequest = new AbortController();
      const controller = this.captionRequest;
      this.captionTimer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('VTT request failed');
        const body = await response.text();
        if (!/^\uFEFF?WEBVTT(?:\s|$)/.test(body)) throw new Error('Invalid VTT');

        const clock = value => value.split(':').reduce((total, number) => total * 60 + Number(number), 0);
        const cues = [];
        for (const block of body.replace(/\r/g, '').split(/\n\s*\n/)) {
          const lines = block.split('\n');
          if (/^(NOTE|STYLE|REGION)(\s|$)/.test(lines[0])) continue;
          const timingIndex = lines.findIndex(line => line.includes('-->'));
          if (timingIndex < 0) continue;
          const match = lines[timingIndex].match(/((?:\d{2,}:)?\d{2}:\d{2}\.\d{3})\s+-->\s+((?:\d{2,}:)?\d{2}:\d{2}\.\d{3})/);
          if (!match) throw new Error('Invalid cue timing');
          const source = lines.slice(timingIndex + 1).join('\n').replace(/<[^>]*>/g, '');
          const text = new DOMParser().parseFromString(source, 'text/html').body.textContent;
          cues.push({ start: clock(match[1]), end: clock(match[2]), text });
        }
        if (this.mediaEpoch !== epoch) return;
        this.cues = cues;
        this.captionState = 'ready';
      } catch (_) {
        if (this.mediaEpoch !== epoch) return;
        this.captionState = 'failed';
      } finally {
        if (this.mediaEpoch === epoch) {
          clearTimeout(this.captionTimer);
          this.render();
        }
      }
    }

    formatTime(seconds) {
      const safe = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
      return `${Math.floor(safe / 60)}:${String(Math.floor(safe % 60)).padStart(2, '0')}`;
    }

    render() {
      if (!this.book) return;
      const audio = this.audio;
      const duration = this.duration;
      const currentTime = Math.min(audio?.currentTime || 0, duration || Infinity);
      const playing = Boolean(audio && !audio.paused && !this.sampleEnded);
      this.dataset.mdsv2State = this.mediaState;
            const art = this.getPart('art');
      const showEnding = Boolean(
        this.sampleEnded &&
        !this.mediaError &&
        this.book.audioEndTemplate?.content.childElementCount
      );

      if (art.classList.contains('mdsv2__listener-art--ending') !== showEnding) {
        art.classList.toggle('mdsv2__listener-art--ending', showEnding);

        const template = showEnding
          ? this.book.audioEndTemplate
          : this.book.artworkTemplate;

        art.replaceChildren(
          template?.content.cloneNode(true) || document.createTextNode('')
        );
        art.scrollTop = 0;
      }

      const play = this.getPart('play');
      play.textContent = playing ? 'Ⅱ' : '▶';
      play.setAttribute('aria-label', playing ? 'Pause sample' : 'Play sample');
      play.disabled = !audio || (this.restoreTime > 0 && !this.canSeek);

      const progress = this.getPart('progress');
      progress.disabled = !this.canSeek;
      progress.max = String(duration || 1);
      progress.value = String(currentTime);
      progress.setAttribute('aria-valuetext', `${this.formatTime(currentTime)} of ${duration ? this.formatTime(duration) : 'unknown duration'}`);
      progress.style.setProperty('--mdsv2-progress', `${duration ? Math.min(100, currentTime / duration * 100) : 0}%`);

      ['start', 'end', 'rewind', 'forward'].forEach(key => {
        this.getPart(key).disabled = !this.canSeek;
      });
      this.getPart('current').textContent = this.formatTime(currentTime);
      this.getPart('duration').textContent = duration ? this.formatTime(duration) : '—:—';

      const speed = this.getPart('speed');
      speed.textContent = `${this.speed}×`;
      speed.setAttribute('aria-label', `Change playback speed. Current speed ${this.speed} times`);

      const captions = this.getPart('cc');
      captions.setAttribute('aria-pressed', String(this.captionsEnabled));
      captions.setAttribute('aria-label', this.captionsEnabled ? 'Turn captions off' : 'Turn captions on');

      const stateText = {
        loading: 'Loading audio…',
        playing: 'Playing',
        paused: currentTime ? 'Paused' : 'Ready to play',
        ended: 'End of sample',
        unavailable: 'Audio is not available for this book.'
      };
      const status = this.mediaError || stateText[this.mediaState] || '';
      if (this.getPart('status').textContent !== status) this.getPart('status').textContent = status;
      const mobileStatus = this.getPart('mobile-status');
      if (mobileStatus && mobileStatus.textContent !== status) mobileStatus.textContent = status;

      let caption;
      if (!this.captionsEnabled) caption = 'Captions are off.';
      else if (this.captionState === 'failed') caption = 'Captions could not be loaded. Audio can still play.';
      else if (this.captionState === 'missing') caption = 'No captions provided for this sample.';
      else if (this.captionState === 'loading') caption = 'Loading captions…';
      else {
        caption = this.cues
          .filter(cue => currentTime >= cue.start && currentTime < cue.end)
          .map(cue => cue.text)
          .join('\n') || (this.mediaState === 'ended' ? 'End of captions.' : 'Captions will appear here.');
      }
      if (this.getPart('caption').textContent !== caption) this.getPart('caption').textContent = caption;
      this.getPart('caption').dataset.mdsv2CaptionState = !this.captionsEnabled ? 'off'
        : this.captionState === 'ready' && ((!currentTime && !playing) || !this.cues.some(cue => currentTime >= cue.start && currentTime < cue.end)) ? 'idle' : 'visible';
    }

    renderReader() {
      const expanded = this.expandedBooks.has(this.activeId);
      this.getPart('reader').dataset.expanded = String(expanded);
      const toggle = this.getPart('read-toggle');
      if (!toggle) return;
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.textContent = expanded ? 'Collapse sample' : 'Read the full sample';
      // Clipped prose must not leave an invisible purchase link in the tab order.
      this.reader.querySelectorAll('a').forEach(link => {
        if (this.mobileQuery.matches && !expanded) link.setAttribute('tabindex', '-1');
        else link.removeAttribute('tabindex');
      });
    }

    setupNavigationContext(listen) {
      this.mobileHeader = document.querySelector('.section-header-mobile');
      this.mobileToolbar = document.querySelector('.halo-sticky-toolbar-mobile');
      this.sectionRoot = this.closest('.mdsv2');
      const schedule = () => {
        cancelAnimationFrame(this.navigationFrame);
        this.navigationFrame = requestAnimationFrame(() => this.updateNavigationContext());
      };
      listen(window, 'scroll', schedule);
      listen(window, 'resize', schedule);
      listen(this.mobileQuery, 'change', () => { this.renderReader(); schedule(); });
      this.navigationObserver = new MutationObserver(schedule);
      this.navigationObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      if (this.mobileHeader) this.navigationObserver.observe(this.mobileHeader, { attributes: true, attributeFilter: ['class'] });
      if (window.ResizeObserver) {
        this.navigationResize = new ResizeObserver(schedule);
        [this.mobileHeader, this.mobileToolbar, this.sectionRoot].filter(Boolean).forEach(node => this.navigationResize.observe(node));
      }
      schedule();
    }

    clearNavigationContext() {
      [this.mobileHeader, this.mobileToolbar].filter(Boolean).forEach(node => {
        if (node.dataset.mdsv2Context === this.dataset.sectionId) delete node.dataset.mdsv2Context;
      });
      this.sectionRoot?.style.removeProperty('--mdsv2-nav-height');
    }

    updateNavigationContext() {
      if (!this.isConnected || !this.mobileQuery.matches) { this.clearNavigationContext(); return; }
      const bounds = this.sectionRoot.getBoundingClientRect();
      const header = this.mobileHeader?.querySelector('.header-mobile')?.getBoundingClientRect();
      const toolbar = this.mobileToolbar?.getBoundingClientRect();
      const drawerOpen = /(?:menu_open|cart-sidebar-show|search-modal-show|search-open|open_search|show-sidebar|quickSearch)/i.test(document.body.className);
      const apply = (node, active) => {
        if (!node) return;
        if (active) node.dataset.mdsv2Context = this.dataset.sectionId;
        else if (node.dataset.mdsv2Context === this.dataset.sectionId) delete node.dataset.mdsv2Context;
      };
      apply(this.mobileHeader, !drawerOpen && header?.bottom > 0 && bounds.top <= header.bottom && bounds.bottom > header.bottom);
      apply(this.mobileToolbar, !drawerOpen && bounds.top < window.innerHeight && bounds.bottom > (header?.bottom || 0));
      const height = toolbar?.height || 0;
      this.sectionRoot.style.setProperty('--mdsv2-nav-height', `${height}px`);
    }
  }

  customElements.define(elementName, MdCinematicSamplerV2);
})();
